import React from 'react';
import { Book } from '@/types/library';
import { Copy, Check } from 'lucide-react';

interface RecordMetaProps {
  book: Book;
}

const RecordMeta: React.FC<RecordMetaProps> = ({ book }) => {
  const [copiedISBN, setCopiedISBN] = React.useState<string | null>(null);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedISBN(id);
    setTimeout(() => setCopiedISBN(null), 2000);
  };

  return (
    <div className="space-y-8">
      {/* Bibliographic Information */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Bibliographic Information</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {book.publisher && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Publisher</dt>
              <dd className="mt-1 text-sm text-gray-900">{book.publisher}</dd>
            </div>
          )}
          {book.year && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Year</dt>
              <dd className="mt-1 text-sm text-gray-900">{book.year}</dd>
            </div>
          )}
          {book.edition && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Edition</dt>
              <dd className="mt-1 text-sm text-gray-900">{book.edition}</dd>
            </div>
          )}
          {book.pages && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Pages</dt>
              <dd className="mt-1 text-sm text-gray-900">{book.pages}</dd>
            </div>
          )}
          {book.language && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Language</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {book.language === 'en' ? 'English' : book.language}
              </dd>
            </div>
          )}
          {book.loc_class && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Library of Congress Class</dt>
              <dd className="mt-1 text-sm text-gray-900 font-mono">{book.loc_class}</dd>
            </div>
          )}
          {book.dewey && (
            <div>
              <dt className="text-sm font-medium text-gray-500">Dewey Decimal</dt>
              <dd className="mt-1 text-sm text-gray-900 font-mono">{book.dewey}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Subjects */}
      {book.subjects && book.subjects.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Subjects</h3>
          <div className="flex flex-wrap gap-2">
            {book.subjects.map((subject, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
              >
                {subject}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Identifiers */}
      {((book.isbn_10 && book.isbn_10.length > 0) || (book.isbn_13 && book.isbn_13.length > 0)) && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Identifiers</h3>
          <dl className="space-y-3">
            {book.isbn_13 && book.isbn_13.map((isbn, idx) => (
              <div key={`isbn13-${idx}`} className="flex items-center justify-between">
                <div>
                  <dt className="text-sm font-medium text-gray-500">ISBN-13</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">{isbn}</dd>
                </div>
                <button
                  onClick={() => copyToClipboard(isbn, `isbn13-${idx}`)}
                  className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 rounded px-2 py-1"
                  aria-label={`Copy ISBN-13: ${isbn}`}
                >
                  {copiedISBN === `isbn13-${idx}` ? (
                    <><Check className="w-4 h-4" /> Copied</>
                  ) : (
                    <><Copy className="w-4 h-4" /> Copy</>
                  )}
                </button>
              </div>
            ))}
            {book.isbn_10 && book.isbn_10.map((isbn, idx) => (
              <div key={`isbn10-${idx}`} className="flex items-center justify-between">
                <div>
                  <dt className="text-sm font-medium text-gray-500">ISBN-10</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">{isbn}</dd>
                </div>
                <button
                  onClick={() => copyToClipboard(isbn, `isbn10-${idx}`)}
                  className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 rounded px-2 py-1"
                  aria-label={`Copy ISBN-10: ${isbn}`}
                >
                  {copiedISBN === `isbn10-${idx}` ? (
                    <><Check className="w-4 h-4" /> Copied</>
                  ) : (
                    <><Copy className="w-4 h-4" /> Copy</>
                  )}
                </button>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
};

export default RecordMeta;